import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  getFirestore,
  onSnapshot,
  collection,
  query,
  where,
  updateDoc,
  addDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  getStorage,
  uploadString,
  getDownloadURL,
  ref,
} from "firebase/storage";
import { getAuth, signOut } from "firebase/auth";
import { Html5Qrcode } from "html5-qrcode";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import customParseFormat from "dayjs/plugin/customParseFormat";
// Importar plugins para UTC y timezone
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import logo from "../assets/logo.png";
import bg from "../assets/bg-app.png";
import loadingGif from "../assets/carga-gps.gif";
import BusinessForm from "../components/BusinessForm";
import { FaTimes, FaUserCircle } from "react-icons/fa";
import {
  MdOutlineAddBusiness,
  MdQrCodeScanner,
  MdDateRange,
  MdSos,
  MdLogout
} from "react-icons/md";
import cargandoNegocio from "../assets/cargandoNegocio.gif";

// Importaciones de react-datepicker
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import OfflineAlert from "../pages/OfflineAlert";

// Configurar dayjs
dayjs.locale("es");
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const AVERAGE_SPEED_KMH = 25;

// Valida si un teléfono es inválido (más de 10 dígitos o contiene espacios)
function isPhoneInvalid(phone) {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, "");
  return digits.length > 10 || /\s/.test(phone);
}

/* =========================================
   Custom Hook para Geolocalización
========================================= */
const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation || !navigator.permissions) {
      setError("Geolocalización o API de permisos no soportadas.");
      return;
    }
    navigator.permissions.query({ name: "geolocation" }).then((result) => {
      if (result.state === "granted" || result.state === "prompt") {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setLocation({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            });
          },
          (err) => {
            console.error("Error al obtener ubicación:", err);
            setError(err.message);
          }
        );
      } else {
        setError("Permiso denegado para geolocalización.");
      }
    });
  }, []);

  return { location, error };
};

/* =========================================
   Componente AgentDashboard
========================================= */
const AgentDashboard = () => {
  // ----------------- Estados principales (la lista de negocios se basa en los cobros de HOY)
  const [rawBusinesses, setRawBusinesses] = useState([]);
  const [paidBusinessIds, setPaidBusinessIds] = useState([]);
  const [reportedBusinessIds, setReportedBusinessIds] = useState([]);
  const [adeudoBusinessIds, setAdeudoBusinessIds] = useState([]);
  const [assignedBusinesses, setAssignedBusinesses] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dailyTotal, setDailyTotal] = useState(0);

  // Estados para la modal de selección de fecha (solo para la modal)
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalTotal, setModalTotal] = useState(0);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  // Otros estados
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [html5QrCode, setHtml5QrCode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [newBusiness, setNewBusiness] = useState({
    name: "",
    address: "",
    location: "",
    phone: "",
    owner: "",
    type: "",
    quota: 0,
    creatorId: "unknown",
    createdAt: null,
    status: "activo",
    qrUrl: "",
  });
  const [alert, setAlert] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [agentRutas, setAgentRutas] = useState([]);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [activeStatView, setActiveStatView] = useState(null); // null | 'pendientes' | 'cobrados' | 'totalACobrar'
  const [statViewVisible, setStatViewVisible] = useState(10);
  const profileMenuRef = useRef(null);
  const statViewEndRef = useRef(null);

  // Firebase y geolocalización
  const { location: userLocation } = useGeolocation();
  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();
  const user = auth.currentUser;

  // ----------------- Permiso de cámara -----------------
  useEffect(() => {
    if (localStorage.getItem("cameraPermissionAsked")) return;
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          stream.getTracks().forEach((track) => track.stop());
          localStorage.setItem("cameraPermissionAsked", "true");
          console.log("Permiso de cámara otorgado y almacenado.");
        })
        .catch((error) => {
          console.error("Error solicitando permiso de cámara:", error);
        });
    }
  }, []);

  // ----------------- Suscripción a cobros (para la lista principal: HOY) -----------------
  useEffect(() => {
    if (!user) return;
    const queryDate = dayjs(); // Siempre hoy para la consulta principal
    const startOfDay = queryDate.startOf("day").toDate();
    const endOfDay = queryDate.endOf("day").toDate();

    const agentId = user.uid;
    const qCobros = query(
      collection(db, "cobros"),
      where("agentId", "==", agentId),
      where("date", ">=", startOfDay),
      where("date", "<=", endOfDay)
    );

    const unsub = onSnapshot(qCobros, (snapshot) => {
      let total = 0;
      const paymentMap = {};
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.tipo !== "abono") {
          total += parseFloat(data.netAmount) || 0;
          paymentMap[data.businessId] = true;
        }
      });
      setPaidBusinessIds(Object.keys(paymentMap));
      setDailyTotal(total);
    });

    return () => unsub();
  }, [db, user]);

  // ----------------- Suscripción para la modal: Cobros del día seleccionado -----------------
  useEffect(() => {
    // Esta consulta solo se ejecuta cuando la modal está abierta y hay una fecha seleccionada
    if (!user || !isDateModalOpen || !selectedDate) return;

    const queryDate = dayjs(selectedDate);
    const startOfDay = queryDate.startOf("day").toDate();
    const endOfDay = queryDate.endOf("day").toDate();
    const agentId = user.uid;

    const qModalCobros = query(
      collection(db, "cobros"),
      where("agentId", "==", agentId),
      where("date", ">=", startOfDay),
      where("date", "<=", endOfDay)
    );

    const unsub = onSnapshot(qModalCobros, (snapshot) => {
      let total = 0;
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.tipo !== "abono") {
          total += parseFloat(data.netAmount) || 0;
        }
      });
      setModalTotal(total);
    });

    return () => unsub();
  }, [db, user, selectedDate, isDateModalOpen]);

  // ----------------- Suscripción a reportes (HOY) -----------------
  useEffect(() => {
    if (!user) return;
    const agentId = user.uid;
    // Usamos dayjs para obtener el rango en hora local
    const today = dayjs();
    const startOfDay = today.startOf("day").toDate();
    const endOfDay = today.endOf("day").toDate();
    
    const qReportes = query(
      collection(db, "reportes"),
      where("agentId", "==", agentId),
      where("date", ">=", startOfDay),
      where("date", "<=", endOfDay)
    );
    const unsub = onSnapshot(qReportes, (snapshot) => {
      const ids = snapshot.docs
        .map((docSnap) => {
          const reportData = docSnap.data();
          let reportDateObj = null;
          if (reportData.date && reportData.date.toDate) {
            reportDateObj = reportData.date.toDate();
          } else if (reportData.date) {
            reportDateObj = new Date(reportData.date);
          }
          // Convertir el timestamp UTC a hora local usando dayjs.utc() y tz()
          const localReportDate = dayjs.utc(reportDateObj).tz(dayjs.tz.guess());
          if (localReportDate.isSame(dayjs().tz(dayjs.tz.guess()), "day")) {
            return reportData.businessId;
          }
          return null;
        })
        .filter(Boolean);
      setReportedBusinessIds(ids);
    });
    return () => unsub();
  }, [db, user]);

  // ----------------- Suscripción a adeudos (HOY) -----------------
  useEffect(() => {
    if (!user) return;
    const agentId = user.uid;
    const qAdeudos = query(
      collection(db, "adeudos"),
      where("agentId", "==", agentId)
    );
    const unsub = onSnapshot(qAdeudos, (snapshot) => {
      const adeudoMap = {};
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const dateVal = data.date;
        let docDate;
        if (dateVal?.toDate) {
          docDate = dayjs(dateVal.toDate());
        } else if (dateVal instanceof Date) {
          docDate = dayjs(dateVal);
        } else {
          return;
        }
        if (docDate.isValid() && docDate.isSame(dayjs(), "day")) {
          adeudoMap[data.businessId] = true;
        }
      });
      setAdeudoBusinessIds(Object.keys(adeudoMap));
    });
    return () => unsub();
  }, [db, user]);

  // ----------------- Cargar rutas del agente -----------------
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
      if (docSnap.exists()) {
        setAgentRutas(docSnap.data().rutas || []);
      }
    });
    return () => unsub();
  }, [db, user]);

  // ----------------- Suscripción a negocios (por rutas del agente + agentId) -----------------
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, "negocios"), (snapshot) => {
      let all = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      // Filtrar: solo negocios cuya ruta esté asignada al agente
      if (agentRutas.length > 0) {
        all = all.filter((b) => b.ruta && agentRutas.includes(b.ruta));
      } else {
        // Fallback: si el agente no tiene rutas asignadas, no mostrar nada
        all = [];
      }
      setRawBusinesses(all);
      setLoading(false);
    });
    return () => unsub();
  }, [db, user, agentRutas]);

  // ----------------- Filtrar negocios asignados (HOY) -----------------
  useEffect(() => {
    if (!rawBusinesses.length) {
      setAssignedBusinesses([]);
      return;
    }
    // Filtramos negocios que NO estén en paidBusinessIds, reportedBusinessIds ni adeudoBusinessIds
    const assigned = rawBusinesses.filter((b) => {
      if (paidBusinessIds.includes(b.id)) return false;
      if (reportedBusinessIds.includes(String(b.id))) return false;
      if (adeudoBusinessIds.includes(b.id)) return false;
      return true;
    });
    setAssignedBusinesses(assigned);
  }, [rawBusinesses, paidBusinessIds, reportedBusinessIds, adeudoBusinessIds]);

  // ----------------- Cálculo de distancia (Fórmula Haversine) -----------------
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (val) => (val * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // ----------------- Calcular distancia y ordenar negocios (useMemo = sin re-render extra) -----------------
  const filteredBusinesses = useMemo(() => {
    if (!assignedBusinesses.length) return [];
    const { latitude, longitude } = userLocation || {};
    const todayName = dayjs().format("dddd").toLowerCase();

    return assignedBusinesses
      .filter((b) => {
        if (b.status !== "activo") return false;
        if (b.schedule && b.schedule.days) {
          const scheduleDays = b.schedule.days.map((d) => d.toLowerCase());
          if (!scheduleDays.includes(todayName)) return false;
        }
        return true;
      })
      .map((b) => {
        let isOpen = true;
        let scheduleHint = null;

        if (b.schedule && b.schedule.openingTime && b.schedule.closingTime) {
          const [openHour, openMinute] = b.schedule.openingTime.split(":").map(Number);
          const [closeHour, closeMinute] = b.schedule.closingTime.split(":").map(Number);
          const now = dayjs();
          const opening = dayjs().hour(openHour).minute(openMinute).second(0);
          let closing = dayjs().hour(closeHour).minute(closeMinute).second(0);
          if (closing.isBefore(opening)) closing = closing.add(1, "day");

          if (now.isBefore(opening)) {
            isOpen = false;
            scheduleHint = `Abre a las ${opening.format("h:mm a")}`;
          } else if (now.isAfter(closing)) {
            isOpen = false;
            scheduleHint = `Cerró a las ${closing.format("h:mm a")}`;
          }
        }

        let distKM = null;
        let timeMin = null;
        if (b.location && latitude && longitude) {
          try {
            const [lat2, lon2] = b.location
              .replace("Lat: ", "")
              .replace("Lng: ", "")
              .split(", ")
              .map(Number);
            if (!isNaN(lat2) && !isNaN(lon2)) {
              distKM = calculateDistance(latitude, longitude, lat2, lon2);
              timeMin = Math.round((distKM / AVERAGE_SPEED_KMH) * 60);
            }
          } catch (e) {
            // location format inválido, ignorar
          }
        }

        return {
          ...b,
          distance: distKM !== null ? distKM.toFixed(2) : null,
          time: timeMin,
          isOpen,
          scheduleHint,
        };
      })
      .sort((a, b) => {
        const aHasLoc = a.distance !== null;
        const bHasLoc = b.distance !== null;
        if (aHasLoc !== bHasLoc) return aHasLoc ? -1 : 1;
        if (aHasLoc && bHasLoc) {
          if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
          return parseFloat(a.distance) - parseFloat(b.distance);
        }
        if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [assignedBusinesses, userLocation]);

  // ----------------- Escáner QR -----------------
  useEffect(() => {
    if (!showScanner) return;
    const qrCode = new Html5Qrcode("reader");
    setHtml5QrCode(qrCode);
    qrCode
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 250 },
        (decodedText) => {
          setShowScanner(false);
          qrCode
            .stop()
            .then(() => qrCode.clear())
            .catch(console.error);
          navigate(`/negocio/${decodedText}`);
        },
        (err) => console.warn("Escaneo fallido:", err)
      )
      .catch((err) => console.error("No se encontraron cámaras:", err));
    return () => {
      if (qrCode && qrCode.isScanning) {
        qrCode
          .stop()
          .then(() => qrCode.clear())
          .catch(console.error);
      }
    };
  }, [showScanner, navigate]);

  // ----------------- Manejo del formulario "nuevo negocio" -----------------
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBusiness({ ...newBusiness, [name]: value });
  };

  const handleNextStep = () => {
    if (currentStep < 2) setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleFormSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newBusiness.schedule?.days || newBusiness.schedule.days.length === 0) {
      setAlert({
        type: "error",
        message: "Por favor selecciona al menos un día de apertura.",
      });
      return;
    }
    try {
      const userId = user ? user.uid : "unknown";
      const timestamp = new Date().toISOString();
      const businessData = {
        ...newBusiness,
        createdAt: timestamp,
        creatorId: userId,
        agentId: userId,
        ruta: agentRutas.length > 0 ? agentRutas[0] : "",
      };

      // 1. Crear el negocio en Firestore (lo único que bloquea)
      const docRef = await addDoc(collection(db, "negocios"), businessData);

      // 2. Mostrar éxito INMEDIATAMENTE — el QR se genera en background
      setCurrentStep(3);
      setAlert({ type: "success", message: "Negocio registrado exitosamente." });

      // 3. Generar QR y subir en background (no bloquea al cobrador)
      (async () => {
        try {
          const [{ default: QRCode }, { default: logoSrc }] = await Promise.all([
            import("qrcode"),
            import("../assets/logoQr.png"),
          ]);

          const canvas = document.createElement("canvas");
          canvas.width = 300;
          canvas.height = 400;
          const context = canvas.getContext("2d");
          await QRCode.toCanvas(canvas, docRef.id, { width: 300 });

          const logoImage = await loadImage(logoSrc);
          const logoSize = 40;
          const logoX = (canvas.width - logoSize) / 2;
          const logoY = (300 - logoSize) / 2;
          context.fillStyle = "white";
          context.fillRect(logoX, logoY, logoSize, logoSize);
          context.drawImage(logoImage, logoX, logoY, logoSize, logoSize);

          const businessName = newBusiness.name?.trim() || "Nombre no disponible";
          context.fillStyle = "#861E3D";
          context.font = "bold 18px Arial";
          context.textAlign = "center";
          context.fillText(businessName, canvas.width / 2, 290);

          const qrBase64 = canvas.toDataURL("image/png");
          const storage = getStorage();
          const qrStorageRef = ref(storage, `qr_codes/${docRef.id}.png`);
          await uploadString(qrStorageRef, qrBase64.split(",")[1], "base64");
          const qrUrl = await getDownloadURL(qrStorageRef);
          await updateDoc(doc(db, "negocios", docRef.id), { qrUrl });
          setNewBusiness((prev) => ({ ...prev, qrUrl }));
        } catch (bgErr) {
          console.error("Error generando QR en background:", bgErr);
        }
      })();
    } catch (error) {
      console.error("Error al registrar negocio:", error);
      setAlert({
        type: "error",
        message: "Error al registrar el negocio.",
      });
    }
  };

  // ----------------- Debounce de búsqueda (300ms) -----------------
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Resultados de búsqueda (useMemo, sin re-render extra)
  const searchResults = useMemo(() => {
    if (!debouncedSearch) return [];
    const queryLower = debouncedSearch.toLowerCase();
    return rawBusinesses.filter((business) => {
      return (
        (business.name && business.name.toLowerCase().includes(queryLower)) ||
        (business.owner && business.owner.toLowerCase().includes(queryLower)) ||
        (business.type && business.type.toLowerCase().includes(queryLower)) ||
        (business.address && business.address.toLowerCase().includes(queryLower))
      );
    });
  }, [debouncedSearch, rawBusinesses]);

  // Cerrar menú de perfil al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Contadores para stat cards
  const statsData = useMemo(() => {
    const todayName = dayjs().format("dddd").toLowerCase();

    const isOpenToday = (b) => {
      if (b.schedule && b.schedule.days) {
        const scheduleDays = b.schedule.days.map((d) => d.toLowerCase());
        if (!scheduleDays.includes(todayName)) return false;
      }
      return true;
    };

    let pendientes = 0;
    let totalACobrar = 0;

    rawBusinesses.forEach((b) => {
      if (b.status !== "activo") return;
      if (!isOpenToday(b)) return;

      // Total a Cobrar = todos los negocios activos y abiertos hoy
      totalACobrar++;

      // Pendientes = activos, abiertos hoy y sin reporte del día
      if (!reportedBusinessIds.includes(String(b.id)) &&
          !paidBusinessIds.includes(b.id) &&
          !adeudoBusinessIds.includes(b.id)) {
        pendientes++;
      }
    });

    return { pendientes, cobrados: paidBusinessIds.length, totalACobrar };
  }, [rawBusinesses, paidBusinessIds, reportedBusinessIds, adeudoBusinessIds]);

  // Lista completa de negocios pendientes (para la vista de pendientes)
  const pendientesList = useMemo(() => {
    const todayName = dayjs().format("dddd").toLowerCase();
    return rawBusinesses.filter((b) => {
      if (b.status !== "activo") return false;
      if (b.schedule && b.schedule.days) {
        const scheduleDays = b.schedule.days.map((d) => d.toLowerCase());
        if (!scheduleDays.includes(todayName)) return false;
      }
      if (reportedBusinessIds.includes(String(b.id))) return false;
      if (paidBusinessIds.includes(b.id)) return false;
      if (adeudoBusinessIds.includes(b.id)) return false;
      return true;
    });
  }, [rawBusinesses, paidBusinessIds, reportedBusinessIds, adeudoBusinessIds]);

  // Lista de negocios cobrados hoy
  const cobradosList = useMemo(() => {
    return rawBusinesses.filter((b) => paidBusinessIds.includes(b.id));
  }, [rawBusinesses, paidBusinessIds]);

  // Lista de todos los negocios activos y abiertos hoy (Total a Cobrar)
  const totalACobrarList = useMemo(() => {
    const todayName = dayjs().format("dddd").toLowerCase();
    return rawBusinesses.filter((b) => {
      if (b.status !== "activo") return false;
      if (b.schedule && b.schedule.days) {
        const scheduleDays = b.schedule.days.map((d) => d.toLowerCase());
        if (!scheduleDays.includes(todayName)) return false;
      }
      return true;
    });
  }, [rawBusinesses]);

  // Lista activa según la vista seleccionada
  const activeList = useMemo(() => {
    if (activeStatView === "pendientes") return pendientesList;
    if (activeStatView === "cobrados") return cobradosList;
    if (activeStatView === "totalACobrar") return totalACobrarList;
    return [];
  }, [activeStatView, pendientesList, cobradosList, totalACobrarList]);

  // Infinite scroll: cargar más items al llegar al final
  const loadMoreStatView = useCallback(() => {
    setStatViewVisible((prev) => Math.min(prev + 10, activeList.length));
  }, [activeList.length]);

  useEffect(() => {
    if (!activeStatView || !statViewEndRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreStatView();
      },
      { threshold: 0.1 }
    );
    observer.observe(statViewEndRef.current);
    return () => observer.disconnect();
  }, [activeStatView, loadMoreStatView]);

if (loading) {
  return (
    <>
      <OfflineAlert />
      <div className="flex items-center justify-center min-h-screen bg-white">
        <img src={loadingGif} alt="Cargando..." className="w-32 h-32" />
      </div>
    </>
  );
}

return (
  <>
    <OfflineAlert />
    <div
      className="min-h-screen flex flex-col text-white"
      style={{
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >

      {/* Header */}
      <header className="sticky top-0 z-40 w-full px-4 py-3 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, rgba(112,23,48,0.95), rgba(134,30,61,0.9))", backdropFilter: "blur(10px)" }}>
        <img src={logo} alt="Logo" className="h-10 object-contain" />

        {/* Círculo de perfil + menú */}
        <div className="relative" ref={profileMenuRef}>
          <button
            onClick={() => setProfileMenuOpen((prev) => !prev)}
            className="w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-all active:scale-95 focus:outline-none"
          >
            <FaUserCircle size={24} />
          </button>

          {profileMenuOpen && (
            <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 overflow-hidden">
              <button
                onClick={() => { setIsModalOpen(true); setProfileMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full transition-colors"
              >
                <MdOutlineAddBusiness size={18} className="text-[#701730]" />
                Registrar negocio
              </button>
              <button
                onClick={() => { setShowScanner(true); setProfileMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full transition-colors"
              >
                <MdQrCodeScanner size={18} className="text-[#701730]" />
                Escanear QR
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => { setProfileMenuOpen(false); signOut(auth).then(() => navigate("/login")); }}
                className="flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
              >
                <MdLogout size={18} />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Contenedor principal */}
      <div className="flex-1 w-full px-4 pt-5 pb-24" style={{ background: "linear-gradient(180deg, rgba(92,3,30,0.8) 0%, rgba(60,2,20,0.9) 100%)" }}>

        {/* Tarjeta de ingresos */}
        <div className="mx-auto max-w-md mb-4">
          <div className="relative overflow-hidden rounded-2xl p-4"
            style={{ background: "linear-gradient(135deg, #BB7820, #D4922A)" }}>
            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
            <div className="absolute -left-4 -bottom-4 w-16 h-16 rounded-full bg-white/10" />
            <div className="relative">
              <p className="text-white/80 text-xs font-medium uppercase tracking-wider mb-1">Ingresos de hoy</p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-white">
                  $ {dailyTotal.toFixed(2)}
                </span>
                <button
                  onClick={() => setIsDateModalOpen(true)}
                  className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl hover:bg-white/30 transition-all active:scale-95"
                >
                  <MdDateRange size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mini stat cards */}
        <div className="mx-auto max-w-md mb-5 grid grid-cols-3 gap-2">
          <button
            onClick={() => { setActiveStatView((prev) => prev === "totalACobrar" ? null : "totalACobrar"); setStatViewVisible(10); }}
            className={`relative overflow-hidden rounded-xl p-3 text-left active:scale-95 transition-all ${activeStatView === "totalACobrar" ? "ring-2 ring-white/60 scale-[0.97]" : ""}`}
            style={{ background: "linear-gradient(135deg, #0077b6, #0096c7)" }}>
            <div className="absolute -right-3 -top-3 w-10 h-10 rounded-full bg-white/10" />
            <p className="text-white/80 text-[9px] font-medium uppercase tracking-wider">Total a Cobrar</p>
            <p className="text-xl font-bold text-white mt-0.5">{statsData.totalACobrar}</p>
          </button>
          <button
            onClick={() => { setActiveStatView((prev) => prev === "pendientes" ? null : "pendientes"); setStatViewVisible(10); }}
            className={`relative overflow-hidden rounded-xl p-3 text-left active:scale-95 transition-all ${activeStatView === "pendientes" ? "ring-2 ring-white/60 scale-[0.97]" : ""}`}
            style={{ background: "linear-gradient(135deg, #e85d04, #f48c06)" }}>
            <div className="absolute -right-3 -top-3 w-10 h-10 rounded-full bg-white/10" />
            <p className="text-white/80 text-[9px] font-medium uppercase tracking-wider">Pendientes</p>
            <p className="text-xl font-bold text-white mt-0.5">{statsData.pendientes}</p>
          </button>
          <button
            onClick={() => { setActiveStatView((prev) => prev === "cobrados" ? null : "cobrados"); setStatViewVisible(10); }}
            className={`relative overflow-hidden rounded-xl p-3 text-left active:scale-95 transition-all ${activeStatView === "cobrados" ? "ring-2 ring-white/60 scale-[0.97]" : ""}`}
            style={{ background: "linear-gradient(135deg, #2d6a4f, #40916c)" }}>
            <div className="absolute -right-3 -top-3 w-10 h-10 rounded-full bg-white/10" />
            <p className="text-white/80 text-[9px] font-medium uppercase tracking-wider">Cobrados</p>
            <p className="text-xl font-bold text-white mt-0.5">{statsData.cobrados}</p>
          </button>
        </div>

        {/* Vista de stat cards (lazy loaded) */}
        {activeStatView ? (
          (() => {
            const viewConfig = {
              pendientes: {
                title: "Negocios pendientes",
                badgeClass: "bg-orange-500/30 text-orange-200",
                badgeLabel: (n) => `${n} pendiente${n !== 1 ? "s" : ""}`,
                avatarGradient: "from-[#e85d04] to-[#f48c06]",
                statusBadgeClass: "bg-orange-100 text-orange-700",
                statusLabel: "Pendiente",
                emptyIcon: "🎉",
                emptyTitle: "Sin pendientes",
                emptyText: "Todos los negocios han sido atendidos hoy.",
              },
              cobrados: {
                title: "Negocios cobrados",
                badgeClass: "bg-emerald-500/30 text-emerald-200",
                badgeLabel: (n) => `${n} cobrado${n !== 1 ? "s" : ""}`,
                avatarGradient: "from-[#2d6a4f] to-[#40916c]",
                statusBadgeClass: "bg-emerald-100 text-emerald-700",
                statusLabel: "Cobrado",
                emptyIcon: "📋",
                emptyTitle: "Sin cobros",
                emptyText: "Aún no se han registrado cobros hoy.",
              },
              totalACobrar: {
                title: "Total a cobrar",
                badgeClass: "bg-blue-500/30 text-blue-200",
                badgeLabel: (n) => `${n} negocio${n !== 1 ? "s" : ""}`,
                avatarGradient: "from-[#0077b6] to-[#0096c7]",
                statusBadgeClass: "bg-blue-100 text-blue-700",
                statusLabel: "Activo",
                emptyIcon: "📭",
                emptyTitle: "Sin negocios",
                emptyText: "No hay negocios programados para hoy.",
              },
            };
            const cfg = viewConfig[activeStatView];
            const list = activeList;

            return (
              <>
                <div className="flex items-center justify-between max-w-md mx-auto mb-4 px-1">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setActiveStatView(null)}
                      className="w-8 h-8 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 transition-all active:scale-95 text-xs"
                    >
                      ←
                    </button>
                    <h1 className="text-lg font-bold">{cfg.title}</h1>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${cfg.badgeClass}`}>
                    {cfg.badgeLabel(list.length)}
                  </span>
                </div>

                <div className="mx-auto max-w-md">
                  {list.length > 0 ? (
                    <ul className="space-y-3">
                      {list.slice(0, statViewVisible).map((b) => {
                        let distanceInfo = null;
                        if (userLocation && b.location) {
                          try {
                            const [lat2, lon2] = b.location.replace("Lat: ", "").replace("Lng: ", "").split(", ").map(Number);
                            if (!isNaN(lat2) && !isNaN(lon2)) {
                              const distKM = calculateDistance(userLocation.latitude, userLocation.longitude, lat2, lon2);
                              const timeMin = Math.round((distKM / AVERAGE_SPEED_KMH) * 60);
                              distanceInfo = { distanceKM: distKM.toFixed(2), timeMin };
                            }
                          } catch (e) { /* ignorar */ }
                        }
                        const isPaid = paidBusinessIds.includes(b.id);
                        const isReported = reportedBusinessIds.includes(String(b.id));
                        const isAdeudo = adeudoBusinessIds.includes(b.id);

                        // Para totalACobrar, mostrar el estado real del negocio
                        let statusLabel = cfg.statusLabel;
                        let statusBadgeClass = cfg.statusBadgeClass;
                        if (activeStatView === "totalACobrar") {
                          if (isPaid) {
                            statusLabel = "Cobrado";
                            statusBadgeClass = "bg-emerald-100 text-emerald-700";
                          } else if (isReported) {
                            statusLabel = "Reportado";
                            statusBadgeClass = "bg-blue-100 text-blue-700";
                          } else if (isAdeudo) {
                            statusLabel = "Adeudo";
                            statusBadgeClass = "bg-red-100 text-red-600";
                          } else {
                            statusLabel = "Pendiente";
                            statusBadgeClass = "bg-orange-100 text-orange-700";
                          }
                        }

                        return (
                          <li key={b.id}>
                            <Link
                              to={`/negocio/${b.id}`}
                              className="block bg-white rounded-2xl p-4 shadow-md hover:shadow-lg transition-shadow no-underline"
                            >
                              <div className="flex items-center">
                                <div className={`w-12 h-12 flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${cfg.avatarGradient} text-white rounded-xl text-sm font-bold`}>
                                  {b.name.split(" ").map((word) => word.charAt(0)).join("").slice(0, 2)}
                                </div>
                                <div className="flex-1 ml-3 min-w-0">
                                  <h2 className="text-sm font-bold text-gray-800 truncate">{b.name}</h2>
                                  <p className="text-xs text-gray-500 truncate">{b.owner}</p>
                                  {b.phone && (
                                    <p className={`text-xs truncate ${isPhoneInvalid(b.phone) ? "text-red-500 font-semibold" : "text-gray-400"}`}>
                                      {b.phone} {isPhoneInvalid(b.phone) && <span className="text-[9px] font-normal">(inv.)</span>}
                                    </p>
                                  )}
                                  {b.address && <p className="text-xs text-gray-400 truncate">{b.address}</p>}
                                  {distanceInfo && (
                                    <p className="text-xs text-gray-400 mt-0.5">{distanceInfo.distanceKM} km - {distanceInfo.timeMin} min</p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end ml-2">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadgeClass}`}>
                                    {statusLabel}
                                  </span>
                                  {distanceInfo && (
                                    <span className="mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">
                                      {distanceInfo.timeMin} min
                                    </span>
                                  )}
                                </div>
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                      {/* Sentinel para infinite scroll */}
                      {statViewVisible < list.length && (
                        <li ref={statViewEndRef} className="flex justify-center py-4">
                          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        </li>
                      )}
                    </ul>
                  ) : (
                    <div className="text-center py-16">
                      <div className="text-4xl mb-3">{cfg.emptyIcon}</div>
                      <p className="text-lg font-bold text-white">{cfg.emptyTitle}</p>
                      <p className="text-sm text-white/60 mt-1">{cfg.emptyText}</p>
                    </div>
                  )}
                </div>
              </>
            );
          })()
        ) : (
        <>
        {/* Título + contador */}
        <div className="flex items-center justify-between max-w-md mx-auto mb-4 px-1">
          <h1 className="text-lg font-bold">{searchQuery ? "Resultados" : "Próximos a cobrar"}</h1>
          <span className="text-xs bg-white/15 text-white/80 px-2.5 py-1 rounded-full font-medium">
            {searchQuery
              ? `${searchResults.length} resultado${searchResults.length !== 1 ? "s" : ""}`
              : `${Math.min(5, filteredBusinesses.length)} de ${filteredBusinesses.length}`
            }
          </span>
        </div>

        {/* Modal de selección de fecha */}
        {isDateModalOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsDateModalOpen(false);
            }}
          >
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm mx-4 text-black">
              <h2 className="text-lg font-bold mb-4 text-center text-gray-800">
                Consultar ingresos
              </h2>
              <div className="w-full mb-4">
                <DatePicker
                  selected={selectedDate ? new Date(selectedDate) : null}
                  onChange={(date) => setSelectedDate(date ? date.toISOString() : null)}
                  dateFormat="dd/MM/yyyy"
                  className="border border-gray-300 p-3 w-full text-black text-base rounded-xl focus:outline-none focus:ring-2 focus:ring-[#BB7820]"
                  placeholderText="Selecciona una fecha"
                />
              </div>
              {selectedDate && (
                <div className="mb-4 p-3 bg-gray-50 rounded-xl text-center">
                  <p className="text-sm text-gray-500">{dayjs(selectedDate).format("DD [de] MMMM [de] YYYY")}</p>
                  <p className="text-xl font-bold text-[#BB7820] mt-1">$ {modalTotal.toFixed(2)}</p>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setIsDateModalOpen(false)}
                  className="flex-1 py-2.5 bg-[#701730] text-white rounded-xl font-medium text-sm hover:bg-[#861E3D] transition-colors"
                >
                  Aceptar
                </button>
                <button
                  onClick={() => { setSelectedDate(null); setIsDateModalOpen(false); }}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Campo de búsqueda */}
        <div className="mx-auto max-w-md mb-5">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar negocio, dueño o dirección..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="text-gray-800 w-full pl-4 pr-10 py-3 rounded-xl bg-white/95 shadow-lg focus:outline-none focus:ring-2 focus:ring-[#BB7820] text-sm placeholder-gray-400"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <FaTimes size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Alerta */}
        {alert && (
          <div className={`mx-auto max-w-md mb-4 p-3 rounded-xl text-center text-sm font-medium ${
            alert.type === "success"
              ? "bg-green-500/20 border border-green-400/30 text-green-100"
              : "bg-red-500/20 border border-red-400/30 text-red-100"
          }`}>
            {alert.message}
          </div>
        )}

        {/* Lista de negocios */}
        <div className="mx-auto max-w-md">
        {searchQuery ? (
          searchResults.length > 0 ? (
            <ul className="space-y-3">
              {searchResults.map((b) => {
                let distanceInfo = null;
                if (userLocation && b.location && b.status !== "inactivo") {
                  try {
                    const [lat2, lon2] = b.location
                      .replace("Lat: ", "")
                      .replace("Lng: ", "")
                      .split(", ")
                      .map(Number);
                    if (!isNaN(lat2) && !isNaN(lon2)) {
                      const distKM = calculateDistance(userLocation.latitude, userLocation.longitude, lat2, lon2);
                      const timeMin = Math.round((distKM / AVERAGE_SPEED_KMH) * 60);
                      distanceInfo = { distanceKM: distKM.toFixed(2), timeMin };
                    }
                  } catch (e) { /* ignorar */ }
                }
                let isOpen = true;
                let scheduleHint = null;
                if (b.schedule && b.schedule.openingTime && b.schedule.closingTime) {
                  const todayName = dayjs().format("dddd").toLowerCase();
                  const scheduleDays = b.schedule?.days?.map((d) => d.toLowerCase()) || [];
                  const isScheduledToday = !b.schedule.days || scheduleDays.includes(todayName);

                  if (!isScheduledToday) {
                    isOpen = false;
                    scheduleHint = "No abre hoy";
                  } else {
                    const [openHour, openMinute] = b.schedule.openingTime.split(":").map(Number);
                    const [closeHour, closeMinute] = b.schedule.closingTime.split(":").map(Number);
                    const now = dayjs();
                    const opening = dayjs().hour(openHour).minute(openMinute).second(0);
                    let closing = dayjs().hour(closeHour).minute(closeMinute).second(0);
                    if (closing.isBefore(opening)) closing = closing.add(1, "day");
                    if (now.isBefore(opening)) {
                      isOpen = false;
                      scheduleHint = `Abre a las ${opening.format("h:mm a")}`;
                    } else if (now.isAfter(closing)) {
                      isOpen = false;
                      scheduleHint = `Cerró a las ${closing.format("h:mm a")}`;
                    }
                  }
                }
                const isPaid = paidBusinessIds.includes(b.id);
                const isReported = reportedBusinessIds.includes(String(b.id));
                return (
                  <li key={b.id}>
                    <Link
                      to={`/negocio/${b.id}`}
                      className="block bg-white rounded-2xl p-4 shadow-md hover:shadow-lg transition-shadow no-underline"
                    >
                      <div className="flex items-center">
                        <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-[#701730] to-[#a3243e] text-white rounded-xl text-sm font-bold">
                          {b.name.split(" ").map((word) => word.charAt(0)).join("").slice(0, 2)}
                        </div>
                        <div className="flex-1 ml-3 min-w-0">
                          <h2 className="text-sm font-bold text-gray-800 truncate">{b.name}</h2>
                          <p className="text-xs text-gray-500 truncate">{b.owner}</p>
                          {b.phone && (
                            <p className={`text-xs truncate ${isPhoneInvalid(b.phone) ? "text-red-500 font-semibold" : "text-gray-400"}`}>
                              {b.phone} {isPhoneInvalid(b.phone) && <span className="text-[9px] font-normal">(inv.)</span>}
                            </p>
                          )}
                          {b.address && <p className="text-xs text-gray-400 truncate">{b.address}</p>}
                          {distanceInfo && (
                            <p className="text-xs text-gray-400 mt-0.5">{distanceInfo.distanceKM} km - {distanceInfo.timeMin} min</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1.5 ml-2">
                          {scheduleHint ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                              {scheduleHint}
                            </span>
                          ) : (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              isOpen ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"
                            }`}>
                              {isOpen ? "Abierto" : "Cerrado"}
                            </span>
                          )}
                          {b.status === "inactivo" && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">Inactivo</span>
                          )}
                          {isPaid && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">Pagado</span>
                          )}
                          {isReported && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700">Reportado</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-center py-12">
              <p className="text-white/70 text-sm">No se encontraron negocios con esa búsqueda.</p>
            </div>
          )
        ) : filteredBusinesses.length > 0 ? (
          <ul className="space-y-3">
            {filteredBusinesses.slice(0, 5).map((b) => (
              <li key={b.id}>
                <Link
                  to={`/negocio/${b.id}`}
                  className={`block bg-white rounded-2xl p-4 shadow-md hover:shadow-lg transition-shadow no-underline ${!b.isOpen ? "opacity-70" : ""}`}
                >
                  <div className="flex items-center">
                    <div className={`w-12 h-12 flex-shrink-0 flex items-center justify-center text-white rounded-xl text-sm font-bold ${b.isOpen ? "bg-gradient-to-br from-[#701730] to-[#a3243e]" : "bg-gradient-to-br from-gray-400 to-gray-500"}`}>
                      {b.name.split(" ").map((word) => word.charAt(0)).join("").slice(0, 2)}
                    </div>
                    <div className="flex-1 ml-3 min-w-0">
                      <h2 className="text-sm font-bold text-gray-800 truncate">{b.name}</h2>
                      <p className="text-xs text-gray-500 truncate">{b.owner}</p>
                      {b.phone && (
                        <p className={`text-xs truncate ${isPhoneInvalid(b.phone) ? "text-red-500 font-semibold" : "text-gray-400"}`}>
                          {b.phone} {isPhoneInvalid(b.phone) && <span className="text-[9px] font-normal">(inv.)</span>}
                        </p>
                      )}
                      {b.address && <p className="text-xs text-gray-400 truncate">{b.address}</p>}
                      {b.distance !== null && (
                        <p className="text-xs text-gray-400 mt-0.5">{b.distance} km - {b.time} min</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 ml-2">
                      {!b.isOpen && b.scheduleHint && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                          {b.scheduleHint}
                        </span>
                      )}
                      {b.time !== null ? (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${b.isOpen ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {b.time} min
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">
                          Sin ubic.
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : assignedBusinesses.length === 0 && rawBusinesses.length > 0 ? (
          <div className="text-center py-16">
            <div className="text-4xl mb-3">&#127881;</div>
            <p className="text-lg font-bold text-white">Buen trabajo</p>
            <p className="text-sm text-white/60 mt-1">Todos los negocios han sido atendidos hoy.</p>
          </div>
        ) : rawBusinesses.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-base text-white/80">No tienes negocios asignados.</p>
            <p className="text-sm text-white/50 mt-1">Contacta al administrador para que te asignen una ruta.</p>
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-base text-white/80">No hay negocios programados para hoy.</p>
          </div>
        )}
        </div>
        </>
        )}
      </div>

      {/* Modal del escáner QR */}
      {showScanner && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-2xl text-black w-full max-w-md mx-4">
            <h2 className="text-base font-bold mb-4 text-center text-gray-800">
              Escanea el código QR
            </h2>
            <div id="reader" className="w-full rounded-xl overflow-hidden"></div>
            <button
              onClick={() => setShowScanner(false)}
              className="mt-4 px-4 py-2.5 bg-[#701730] text-white rounded-xl hover:bg-[#861E3D] w-full font-medium text-sm transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal para crear negocio */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-[800px] mx-4 relative">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FaTimes size={18} />
            </button>
            <BusinessForm
              newBusiness={newBusiness}
              setNewBusiness={setNewBusiness}
              handleInputChange={handleInputChange}
              handleNextStep={handleNextStep}
              handlePreviousStep={handlePreviousStep}
              handleFormSubmit={handleFormSubmit}
              currentStep={currentStep}
              handleCancel={() => setIsModalOpen(false)}
              agentOptions={[
                {
                  id: user.uid,
                  name: user.displayName || user.email || "Cobrador",
                },
              ]}
            />
          </div>
        </div>
      )}
      <button
        onClick={() => window.open("https://auxsac.igob.mx/", "_blank", "noopener,noreferrer")}
        title="SOS"
        aria-label="SOS"
        className="fixed bottom-6 right-5 z-[110] h-14 w-14 rounded-2xl shadow-xl bg-red-600 text-white flex items-center justify-center hover:bg-red-700 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-white/60"
      >
        <MdSos size={28} />
      </button>
    </div> {/* <--- Este cierra el div grande */}
    </>
  );
};

export default AgentDashboard;
